import React, { useState, useMemo, useCallback } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useSelector } from "react-redux";
import * as kestrelEmbedCode from "shared/kestrel-embed-code";
import { validate } from "shared/yup";
import { getMarketsOptimized } from "redux/selector";
import { WidgetConfigLayout } from "../widget-config-layout";
import {
	WidgetFieldCell,
	WidgetFieldGrid,
	WidgetFieldGroup,
	WidgetNumber,
	WidgetSelect,
} from "../widget-fields";
import { Switch } from "component/shared/switch";
import { getUser } from "redux/selector";
import { ListingSortType } from "type/listing-sort";
import {
	DEFAULT_GALLERY_SORT,
	DEFAULT_SECONDS_PER_PHOTO,
	gallerySliderSchema,
	secondsToInterval,
} from "./settings";
import { styles } from "./style";

const useStyles = makeStyles()(styles);

const GallerySliderConfig: React.FC = () => {
	const { classes } = useStyles();
	// Redux selectors
	const user = useSelector(getUser);
	const markets = useSelector(getMarketsOptimized);

	// Local state
	const [rows, setRows] = useState<number | undefined>(1);
	const [navigation] = useState<boolean>(true);
	const [nav, setNav] = useState<string>("top");
	const [auto, setAuto] = useState<boolean>(false);
	const [interval, setInterval] = useState<number | undefined>(DEFAULT_SECONDS_PER_PHOTO);
	const [maxResults, setMaxResults] = useState<number>(25);
	const [status, setStatus] = useState<string>("active");
	const [sort, setSort] = useState<string>(DEFAULT_GALLERY_SORT);
	const [featured, setFeatured] = useState<boolean>(true);
	const [effect, setEffect] = useState<string>("slide");
	const [marketId, setMarketId] = useState<string>("");

	// Memoized validation schema
	const validationSchema = useMemo(() => gallerySliderSchema(featured), [featured]);

	// Memoized validation state
	const validationState = useMemo(() => ({
		marketId,
		maxResults,
	}), [marketId, maxResults]);

	// Memoized validation errors
	const errors = useMemo(() => validate(validationSchema, validationState), [validationSchema, validationState]);

	// Memoized embed code
	const embedCode = useMemo(() => {
		return kestrelEmbedCode.gallerySliderWidget(
			user.websitePlatform,
			rows ? rows : undefined,
			navigation,
			nav,
			auto,
			// The time per photo only matters, and is only asked for, while photos advance automatically.
			auto ? secondsToInterval(interval) : undefined,
			maxResults,
			status,
			sort ? sort : undefined,
			featured,
			effect,
			marketId ? parseInt(marketId) : undefined,
		);
	}, [user.websitePlatform, rows, navigation, nav, auto, interval, maxResults, status, sort, featured, effect, marketId]);

	// Event handlers
	const handleSourceChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const nextFeatured = event.target.value === "featured";
		setFeatured(nextFeatured);
		if (nextFeatured) {
			setMarketId("");
		}
	}, []);

	return (
		<WidgetConfigLayout user={user} embedCode={!errors ? embedCode : undefined}>
			<WidgetFieldGroup title="Listing Source">
				<Mui.RadioGroup
					value={featured ? "featured" : "market"}
					onChange={handleSourceChange}
				>
					<Mui.FormControlLabel
						value="featured"
						className={classes.radioLabel}
						control={<Mui.Radio color="secondary" />}
						label="Featured Listings"
					/>
					<Mui.FormControlLabel
						value="market"
						className={classes.radioLabel}
						control={<Mui.Radio color="secondary" />}
						label="Market"
					/>
				</Mui.RadioGroup>
				{!featured && (
					<WidgetFieldGrid>
						<WidgetFieldCell full>
							<WidgetSelect
								label="Market"
								required
								value={marketId}
								options={markets.map((market) => ({value: market.id, label: market.name}))}
								onChange={setMarketId}
							/>
						</WidgetFieldCell>
					</WidgetFieldGrid>
				)}
			</WidgetFieldGroup>
			<Mui.Divider />
			<WidgetFieldGroup title="Display Options">
				<WidgetFieldGrid>
					<WidgetFieldCell>
						<WidgetNumber
							label="Rows"
							min={1}
							value={rows}
							onChange={setRows}
						/>
					</WidgetFieldCell>
					<WidgetFieldCell>
						<WidgetNumber
							label="Max Number of Listings"
							required
							min={1}
							value={maxResults}
							onChange={setMaxResults}
						/>
					</WidgetFieldCell>
					<WidgetFieldCell>
						<WidgetSelect
							label="Listing Status"
							value={status}
							options={[{value: "active", label: "Active"}, {value: "sold", label: "Sold"}]}
							onChange={setStatus}
						/>
					</WidgetFieldCell>
					<WidgetFieldCell>
						<WidgetSelect
							label="Sort"
							value={sort}
							options={ListingSortType.values().map((value) => ({value: value.id, label: value.label}))}
							onChange={setSort}
						/>
					</WidgetFieldCell>
				</WidgetFieldGrid>
			</WidgetFieldGroup>
			<Mui.Divider />
			<WidgetFieldGroup title="Navigation">
				<WidgetFieldGrid>
					<WidgetFieldCell>
						<WidgetSelect
							label="Transition Style"
							value={effect}
							options={[{value: "slide", label: "Slide"}, {value: "fade", label: "Fade"}]}
							onChange={setEffect}
						/>
					</WidgetFieldCell>
					<WidgetFieldCell>
						<WidgetSelect
							label="Navigation Position"
							value={nav}
							options={[
								{value: "top", label: "Top"},
								{value: "bottom", label: "Bottom"},
								{value: "sides", label: "Sides"},
							]}
							onChange={setNav}
						/>
					</WidgetFieldCell>
				</WidgetFieldGrid>
				<Switch
					label="Advance photos automatically"
					labelBold={false}
					checked={auto}
					onChange={setAuto}
				/>
				{auto && (
					<div className={classes.indented}>
						<WidgetFieldGrid>
							<WidgetFieldCell>
								<WidgetNumber
									label="Time Per Photo (Seconds)"
									min={1}
									value={interval}
									onChange={setInterval}
								/>
							</WidgetFieldCell>
						</WidgetFieldGrid>
					</div>
				)}
			</WidgetFieldGroup>
		</WidgetConfigLayout>
	);
};

export { GallerySliderConfig };
